import React, { useState } from "react";
import axios from "axios";
import {
	Button,
	TextField,
	MenuItem,
	FormControl,
	Select,
	InputLabel,
	CircularProgress,
	Typography,
	Card,
	CardContent,
	CardHeader,
} from "@mui/material";
import { UploadIcon } from "lucide-react";
import { styled } from "@mui/material/styles";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import BRANDING from "../config/branding";

import { API_URL } from '../config/api';

const SendMultipleSmsPage = () => {
	const [file, setFile] = useState(null);
	const [batchId, setBatchId] = useState("");
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [columns, setColumns] = useState([]);
	const [firstRow, setFirstRow] = useState({});
	const [phoneHeader, setPhoneHeader] = useState("");

	const navigate = useNavigate();

	// Hidden input for file upload
	const VisuallyHiddenInput = styled("input")({
		clip: "rect(0 0 0 0)",
		clipPath: "inset(50%)",
		height: 1,
		overflow: "hidden",
		position: "absolute",
		bottom: 0,
		left: 0,
		whiteSpace: "nowrap",
		width: 1,
	});

	const handleFileChange = (e) => {
		const uploadedFile = e.target.files?.[0];
		if (!uploadedFile) return;

		setFile(uploadedFile);
		setBatchId(new Date().getTime().toString());
		setError(null);

		const reader = new FileReader();
		const isCSV = uploadedFile.name.endsWith(".csv");

		reader.onload = (e) => {
			if (isCSV) {
				const text = e.target.result;
				Papa.parse(text, {
					header: true,
					skipEmptyLines: true,
					complete: function (results) {
						setColumns(results.meta.fields);
						setFirstRow(results.data[0]);
					},
				});
			} else {
				const data = new Uint8Array(e.target.result);
				const workbook = XLSX.read(data, { type: "array" });
				const sheetName = workbook.SheetNames[0];
				const sheet = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], {
					header: 1,
				});

				const headers = sheet[0];
				const values = sheet[1];

				const rowObj = {};
				headers.forEach((key, index) => {
					rowObj[key] = values[index];
				});

				setColumns(headers);
				setFirstRow(rowObj);
			}
		};

		if (isCSV) {
			reader.readAsText(uploadedFile);
		} else {
			reader.readAsArrayBuffer(uploadedFile);
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setLoading(true);
		setError(null);

		if (!file) {
			setError("Please select a file.");
			setLoading(false);
			return;
		}

		if (!phoneHeader) {
			setError("Please specify the phone number column.");
			setLoading(false);
			return;
		}

		if (!columns.includes(phoneHeader)) {
			setError("The specified phone column does not exist in the file.");
			setLoading(false);
			return;
		}

		const formData = new FormData();
		formData.append("file", file);
		formData.append("batchId", batchId);
		formData.append("phoneHeader", phoneHeader);

		try {
			const token = localStorage.getItem("token");
			const response = await axios.post(
				`${API_URL}/queues2.php`,
				formData,
				{
					headers: {
						"Content-Type": "multipart/form-data",
						Authorization: `Bearer ${token}`,
					},
				}
			);

			if (response.data.status) {
				toast.success(response.data.message || "SMS Queued successfully");
				navigate("/dashboard");
			} else {
				setError(response.data.message || "Failed to process file.");
			}
		} catch (err) {
			setError(err.message || "An error occurred during upload.");
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className='flex justify-center items-center min-h-screen bg-white px-4'>
			<div className='flex justify-center mb-6'>

</div>
			<Card className='w-full max-w-lg shadow-2xl rounded-2xl border border-red-200'>
		
				<CardHeader
	title='Send Bulk SMS'
	className='text-center text-3xl font-bold py-4 rounded-t-2xl'
	sx={{
		color: BRANDING.primaryColor,
		backgroundColor: '#FEF2F2',
	}}
/>
<Typography
	variant='body2'
	align='center'
	className='text-gray-500 mb-6'
>
	Upload CSV or Excel files for bulk SMS delivery
</Typography>
				<CardContent>
					{error && (
						<div className='mb-4 text-sm text-red-700 text-center border border-red-400 rounded-md p-3 bg-red-100'>
							<strong>Error:</strong> {error}
						</div>
					)}

					<form onSubmit={handleSubmit} className='space-y-6'>
						<div>
							<Typography
								variant='body2'
								className='mb-2 text-red-800 font-medium'
							>
								Upload File (CSV or Excel)
							</Typography>
							<Button
								component='label'
								fullWidth
								variant='outlined'
								startIcon={<UploadIcon />}
								className='border-red-600 text-red-600 hover:bg-red-600 hover:text-white transition rounded-lg'
							>
								Choose File
								<VisuallyHiddenInput
									type='file'
									accept='.csv,.xlsx,.xls'
									onChange={handleFileChange}
								/>
							</Button>
							{file && (
								<p className='text-xs text-gray-600 mt-2 text-center'>
									Selected:{" "}
									<strong className='text-red-700'>{file.name}</strong>
								</p>
							)}
						</div>

						<TextField
							label='Batch ID'
							value={batchId}
							onChange={(e) => setBatchId(e.target.value)}
							fullWidth
							required
							variant='outlined'
							size='small'
							InputProps={{
								style: { borderRadius: "10px" },
							}}
						/>

						{columns.length > 0 && (
							<FormControl fullWidth size='small'>
								<InputLabel id='phone-col-label'>
									Phone Number Column
								</InputLabel>
								<Select
									labelId='phone-col-label'
									value={phoneHeader}
									onChange={(e) => setPhoneHeader(e.target.value)}
									label='Phone Number Column'
									className='rounded-lg'
								>
									{columns.map((col) => (
										<MenuItem key={col} value={col}>
											{col}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						)}

						<Button
							type='submit'
							fullWidth
							variant='contained'
							disabled={loading}
							className='bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg shadow-md'
						>
							{loading ? (
								<CircularProgress size={24} className='text-white' />
							) : (
								"Submit Bulk SMS"
							)}
						</Button>
					</form>
				</CardContent>
			</Card>
		</div>
	);
};

export default SendMultipleSmsPage;
